import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { QuestionRendererProps } from './types';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, ChevronDown, X } from 'lucide-react';
import { SentenceParserConfig } from '../../../content/types';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
import { useClickOutside } from '../../../app/hooks/useClickOutside';
export function SentenceParserRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = (question.data?.sentenceParser || question.data || {}) as SentenceParserConfig;
  const words = config.words || [];
  const labels = config.labels || [];
  const correctAssignments = config.correctAssignments || {};
  const colors = config.colors || {};
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [activeWordIndex, setActiveWordIndex] = useState<number | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  useClickOutside(popoverRef, () => setActiveWordIndex(null));
  useEffect(() => {
    setAssignments({});
    setActiveWordIndex(null);
  }, [question.id]);
  useEffect(() => {
    // Ready when all required words have a label
    const requiredWords = Object.keys(correctAssignments);
    const allAssigned = requiredWords.every(wordIndex => assignments[wordIndex]);
    onReadyChange?.(allAssigned && requiredWords.length > 0);
  }, [assignments, correctAssignments, onReadyChange]);
  const answerPayload = useMemo(() => JSON.stringify(assignments), [assignments]);
  const handleEdgeSubmit = useCallback((payload: string) => {
    onAnswer(payload);
    setActiveWordIndex(null);
  }, [onAnswer]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, handleEdgeSubmit, question.id);
  const handleWordClick = (wordIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (shouldValidate) return;
    setActiveWordIndex(activeWordIndex === wordIndex ? null : wordIndex);
  };
  const handleLabelSelect = (wordIndex: number, label: string | null) => {
    setAssignments(prev => {
      const next = {
        ...prev
      };
      if (label === null) {
        delete next[wordIndex];
      } else {
        next[wordIndex] = label;
      }
      return next;
    });
    setActiveWordIndex(null);
  };
  const getFullLabelName = (abbr: string) => {
    const map: Record<string, string> = {
      OND: 'Onderwerp (OND)',
      PV: 'Persoonsvorm (PV)',
      LV: 'Lijdend voorwerp (LV)',
      MV: 'Meewerkend voorwerp (MV)',
      BWB: 'Bepaling (BWB)',
      GEZ: 'Gezegde (GEZ)'
    };
    return map[abbr] || abbr;
  };
  if (words.length === 0 || labels.length === 0) {
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-10998">
        <p className="text-red-700 font-bold" data-id="element-10999">
          ⚠️ Configuratie ontbreekt (words/labels)
        </p>
      </div>;
  }
  return <div className="w-full max-w-4xl mx-auto flex flex-col gap-6" data-id="element-11000">
      <div className="bg-slate-50 rounded-2xl p-4 border-2 border-slate-200 shadow-inner text-center" data-id="element-11001">
        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider" data-id="element-11002">
          Tik op een woord om een zinsdeel te kiezen
        </h3>
      </div>

      {/* Sentence Area */}
      <div className="bg-white rounded-3xl p-6 sm:p-10 border-2 border-slate-200 shadow-sm flex flex-wrap gap-x-2 sm:gap-x-3 gap-y-12 justify-center items-start min-h-[200px]" data-id="element-11003">
        {words.map((word, index) => {
        const assignedLabel = assignments[index];
        const isActive = activeWordIndex === index;
        const customColor = assignedLabel ? colors[assignedLabel] : undefined;
        let isCorrect = false;
        let showValidation = false;
        if (shouldValidate && assignedLabel) {
          showValidation = true;
          isCorrect = assignedLabel === correctAssignments[index];
        }
        let wordClass = `text-xl sm:text-2xl font-medium px-2 py-1 rounded-lg transition-all relative flex flex-col items-center `;
        let wordStyle: React.CSSProperties = {};
        if (shouldValidate) {
          wordClass += 'cursor-default ';
        } else {
          wordClass += 'cursor-pointer hover:bg-slate-50 ';
        }
        if (isActive) {
          wordClass += 'bg-slate-100 ring-4 ring-slate-100 ';
        }
        if (assignedLabel) {
          if (customColor) {
            wordStyle = {
              borderBottom: `3px solid ${customColor}`,
              backgroundColor: isActive ? undefined : `${customColor}10`
            };
          } else {
            wordClass += 'border-b-[3px] border-indigo-400 bg-indigo-50/30 ';
          }
        } else {
          wordStyle = {
            borderBottom: '3px solid transparent'
          };
        }
        return <div key={index} className="relative flex flex-col items-center" data-id="element-11004">
              <button onClick={e => handleWordClick(index, e)} disabled={shouldValidate} className={wordClass} style={wordStyle} data-id="element-11005">
                <span className={assignedLabel ? 'text-slate-900' : 'text-slate-700'} data-id="element-11006">
                  {word}
                </span>

                {/* Badge Below Word */}
                <AnimatePresence data-id="element-11007">
                  {assignedLabel && <motion.div initial={{
                opacity: 0,
                y: -5
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -5
              }} className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-1 whitespace-nowrap z-0" data-id="element-11008">
                      <div className={`px-2 py-0.5 rounded-md text-xs sm:text-sm font-bold border shadow-sm flex items-center gap-1 ${showValidation ? isCorrect ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700' : ''}`} style={!showValidation && customColor ? {
                  backgroundColor: `${customColor}15`,
                  borderColor: customColor,
                  color: customColor
                } : !showValidation ? {
                  backgroundColor: '#e0e7ff',
                  borderColor: '#c7d2fe',
                  color: '#3730a3' // indigo-800
                } : {}} data-id="element-11009">
                        {assignedLabel}
                        {showValidation && (isCorrect ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" data-id="element-11010" /> : <XCircle className="w-3.5 h-3.5 text-red-500" data-id="element-11011" />)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </button>

              {/* Popover */}
              <AnimatePresence data-id="element-11012">
                {isActive && !shouldValidate && <motion.div ref={popoverRef} initial={{
              opacity: 0,
              y: 10,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: 10,
              scale: 0.95
            }} transition={{
              duration: 0.15
            }} className="absolute top-full mt-3 left-1/2 -translate-x-1/2 z-50 min-w-[200px] bg-white rounded-2xl shadow-xl border-2 border-slate-200 overflow-hidden flex flex-col" data-id="element-11013">
                    <div className="p-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between" data-id="element-11014">
                      <span className="text-xs font-bold text-slate-500 uppercase px-2" data-id="element-11015">
                        Kies zinsdeel
                      </span>
                      <button onClick={e => {
                  e.stopPropagation();
                  setActiveWordIndex(null);
                }} className="p-1 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-200 transition-colors" data-id="element-11016">
                        <X className="w-4 h-4" data-id="element-11017" />
                      </button>
                    </div>
                    <div className="p-2 flex flex-col gap-1 max-h-[240px] overflow-y-auto" data-id="element-11018">
                      {labels.map(label => {
                  const isSelected = assignedLabel === label;
                  const customColor = colors[label];
                  return <button key={label} onClick={e => {
                    e.stopPropagation();
                    handleLabelSelect(index, label);
                  }} className={`px-3 py-2 rounded-xl text-left text-sm font-bold transition-colors flex items-center justify-between ${isSelected ? 'bg-slate-100' : 'hover:bg-slate-50 text-slate-700'}`} style={isSelected && customColor ? {
                    color: customColor,
                    backgroundColor: `${customColor}15`
                  } : isSelected ? {
                    color: '#4f46e5',
                    backgroundColor: '#e0e7ff'
                  } : {}} data-id="element-11019">
                            {getFullLabelName(label)}
                            {isSelected && <CheckCircle2 className="w-4 h-4" data-id="element-11020" />}
                          </button>;
                })}

                      {assignedLabel && <>
                          <div className="h-px bg-slate-100 my-1 mx-2" data-id="element-11021" />
                          <button onClick={e => {
                    e.stopPropagation();
                    handleLabelSelect(index, null);
                  }} className="px-3 py-2 rounded-xl text-left text-sm font-bold text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2" data-id="element-11022">
                            <XCircle className="w-4 h-4" data-id="element-11023" />
                            Wissen
                          </button>
                        </>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>;
      })}
      </div>
    </div>;
}